import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Check, GraduationCap, Trash2, Users, Eye, X, FileText, Mail, Phone, MapPin, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LESSON_TYPE_LABEL } from "@/constants/options";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, role, name }
  const [detail, setDetail] = useState(null); // { role, data }

  const load = async () => {
    setLoadingList(true);
    try {
      const [t, f] = await Promise.all([
        api.get("/admin/teachers"),
        api.get("/admin/families"),
      ]);
      setTeachers(t.data.teachers || []);
      setFamilies(f.data.families || []);
    } catch {
      toast.error("Liste yüklenemedi");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-400">Yükleniyor...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const verify = async (id) => {
    try {
      await api.post(`/admin/verify/${id}`);
      toast.success("Öğretmen doğrulandı");
      setTeachers((ts) => ts.map((t) => t.user_id === id ? { ...t, verified: true } : t));
    } catch { toast.error("Doğrulama başarısız"); }
  };
  const unverify = async (id) => {
    try {
      await api.post(`/admin/unverify/${id}`);
      setTeachers((ts) => ts.map((t) => t.user_id === id ? { ...t, verified: false } : t));
      toast.success("Doğrulama kaldırıldı");
    } catch {}
  };
  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/user/${confirmDelete.id}`);
      if (confirmDelete.role === "teacher") {
        setTeachers((ts) => ts.filter((t) => t.user_id !== confirmDelete.id));
      } else {
        setFamilies((fs) => fs.filter((f) => f.id !== confirmDelete.id));
      }
      toast.success("Kullanıcı silindi");
    } catch { toast.error("Silinemedi"); } finally { setConfirmDelete(null); }
  };

  const unverifiedT = teachers.filter((t) => !t.verified);
  const verifiedT = teachers.filter((t) => t.verified);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        <div className="reveal">
          <span className="text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold">Yönetim</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter" data-testid="admin-title">Admin Panel</h1>
          <p className="mt-3 text-slate-500">Öğretmenleri doğrula, profilleri incele, kullanıcıları yönet.</p>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-4">
          <StatCard label="Öğretmen" value={teachers.length} accent="var(--brand)" />
          <StatCard label="Aile" value={families.length} accent="var(--accent)" />
          <StatCard label="Doğrulanmış" value={verifiedT.length} accent="var(--mint)" />
        </div>

        <Tabs defaultValue="teachers" className="mt-12">
          <TabsList className="bg-slate-100 p-1 rounded-full h-11" data-testid="admin-tabs">
            <TabsTrigger value="teachers" data-testid="tab-teachers" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <GraduationCap size={14} className="mr-1.5" /> Öğretmenler ({teachers.length})
            </TabsTrigger>
            <TabsTrigger value="families" data-testid="tab-families" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Users size={14} className="mr-1.5" /> Aileler ({families.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teachers" className="mt-8">
            <Section title="Doğrulama bekleyen öğretmenler">
              {loadingList ? <p className="text-sm text-slate-500">Yükleniyor...</p> :
                unverifiedT.length === 0 ? <EmptyRow label="Bekleyen öğretmen yok" /> :
                unverifiedT.map((t) => (
                  <TeacherRow key={t.user_id} t={t}
                    onOpen={() => setDetail({ role: "teacher", data: t })}
                    onVerify={() => verify(t.user_id)}
                    onDelete={() => setConfirmDelete({ id: t.user_id, role: "teacher", name: t.name })}
                  />
                ))
              }
            </Section>
            <Section title="Doğrulanmış öğretmenler">
              {verifiedT.length === 0 ? <EmptyRow label="Henüz doğrulanmış öğretmen yok" /> :
                verifiedT.map((t) => (
                  <TeacherRow key={t.user_id} t={t} verified
                    onOpen={() => setDetail({ role: "teacher", data: t })}
                    onUnverify={() => unverify(t.user_id)}
                    onDelete={() => setConfirmDelete({ id: t.user_id, role: "teacher", name: t.name })}
                  />
                ))
              }
            </Section>
          </TabsContent>

          <TabsContent value="families" className="mt-8">
            <Section title="Kayıtlı aileler">
              {loadingList ? <p className="text-sm text-slate-500">Yükleniyor...</p> :
                families.length === 0 ? <EmptyRow label="Henüz aile kaydı yok" /> :
                families.map((f) => (
                  <FamilyRow key={f.id} f={f}
                    onOpen={() => setDetail({ role: "family", data: f })}
                    onDelete={() => setConfirmDelete({ id: f.id, role: "family", name: f.name })}
                  />
                ))
              }
            </Section>
          </TabsContent>
        </Tabs>
      </div>

      {/* DETAIL MODAL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-6 pb-2 border-b border-slate-200">
            <DialogTitle className="font-display text-2xl">
              {detail?.role === "teacher" ? "Öğretmen Profili" : "Aile Profili"}
            </DialogTitle>
            <DialogDescription>
              {detail?.role === "teacher" ? "Öğretmenin tüm profil bilgileri." : "Ailenin tüm bilgileri ve çocuklarının detayları."}
            </DialogDescription>
          </DialogHeader>
          {detail && detail.role === "teacher" && <TeacherDetailModal t={detail.data} />}
          {detail && detail.role === "family" && <FamilyDetailModal f={detail.data} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Kullanıcıyı silmek istediğine emin misin?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-slate-900">{confirmDelete?.name}</strong> adlı{" "}
              {confirmDelete?.role === "teacher" ? "öğretmen" : "aile"} hesabı ve ilişkili tüm veriler
              (profil, favoriler, yorumlar, rezervasyonlar) kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={performDelete} data-testid="delete-confirm"
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600">Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-slate-200 rounded-2xl p-6 bg-white">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</div>
      <div className="mt-2 font-display text-4xl font-semibold" style={{ color: accent }}>{value}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="mt-8 first:mt-0">
      <h2 className="font-display text-2xl font-semibold mb-4">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function EmptyRow({ label }) {
  return <div className="border border-dashed border-slate-300 rounded-xl p-6 text-center text-sm text-slate-500">{label}</div>;
}

function TeacherRow({ t, verified, onOpen, onVerify, onUnverify, onDelete }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white flex items-center gap-4 hover:border-[color:var(--brand)]/30 transition-colors" data-testid={`admin-row-${t.user_id}`}>
      <button
        onClick={onOpen}
        className="h-14 w-14 rounded-full overflow-hidden bg-slate-100 grid place-items-center flex-shrink-0 hover:ring-2 hover:ring-[color:var(--brand)]/40 transition-all"
        data-testid={`open-teacher-${t.user_id}`}
        aria-label="Profili gör"
      >
        {t.photo_path ? <img src={fileUrl(t.photo_path)} alt="" className="w-full h-full object-cover" /> : <GraduationCap size={22} className="text-slate-400" />}
      </button>
      <button onClick={onOpen} className="flex-1 min-w-0 text-left" data-testid={`open-teacher-name-${t.user_id}`}>
        <div className="flex items-center gap-2">
          <div className="font-medium truncate hover:text-[color:var(--brand)] transition-colors">{t.name}</div>
          {verified && <ShieldCheck size={14} className="text-[color:var(--mint)]" />}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {t.university}{t.department ? ` · ${t.department}` : ""} · {(t.subjects || []).join(", ")}
        </div>
      </button>
      <div className="flex items-center gap-2">
        <button onClick={onOpen} data-testid={`view-teacher-${t.user_id}`}
          className="h-10 w-10 rounded-full border border-slate-200 text-slate-600 hover:text-[color:var(--brand)] hover:border-[color:var(--brand)]/40 grid place-items-center transition-colors"
          aria-label="Profili gör"><Eye size={16} /></button>
        {verified ? (
          <button onClick={onUnverify} data-testid={`unverify-${t.user_id}`}
            className="text-xs font-medium text-slate-500 hover:text-red-600 px-3 py-2 rounded-full border border-slate-200 hover:border-red-300 transition-colors">Doğrulamayı Kaldır</button>
        ) : (
          <button onClick={onVerify} data-testid={`verify-${t.user_id}`}
            className="inline-flex items-center gap-1.5 bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white rounded-full px-4 h-10 text-sm font-semibold active:scale-95 transition-transform"><Check size={14} /> Doğrula</button>
        )}
        <button onClick={onDelete} data-testid={`delete-teacher-${t.user_id}`}
          className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-300 transition-colors"
          aria-label="Sil"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}

function FamilyRow({ f, onOpen, onDelete }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white flex items-center gap-4 hover:border-[color:var(--accent)]/30 transition-colors" data-testid={`admin-family-${f.id}`}>
      <button onClick={onOpen} className="h-14 w-14 rounded-full bg-[color:var(--accent)]/10 grid place-items-center flex-shrink-0 hover:ring-2 hover:ring-[color:var(--accent)]/40 transition-all" data-testid={`open-family-${f.id}`}>
        <Users size={22} className="text-[color:var(--accent)]" />
      </button>
      <button onClick={onOpen} className="flex-1 min-w-0 text-left" data-testid={`open-family-name-${f.id}`}>
        <div className="font-medium truncate hover:text-[color:var(--brand)] transition-colors">{f.name}</div>
        <div className="text-xs text-slate-500 truncate">{f.email} · {f.phone} · {f.child_count || 0} çocuk</div>
      </button>
      <div className="flex items-center gap-2">
        <button onClick={onOpen} data-testid={`view-family-${f.id}`}
          className="h-10 w-10 rounded-full border border-slate-200 text-slate-600 hover:text-[color:var(--brand)] hover:border-[color:var(--brand)]/40 grid place-items-center transition-colors"><Eye size={16} /></button>
        <button onClick={onDelete} data-testid={`delete-family-${f.id}`}
          className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-300 rounded-full px-4 h-10 text-sm font-medium transition-colors"><Trash2 size={14} /> Sil</button>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
      <div className="mt-1 text-sm text-slate-900">{value}</div>
    </div>
  );
}

function Chip({ children }) {
  return <span className="text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50">{children}</span>;
}

function TeacherDetailModal({ t }) {
  return (
    <div className="p-6 space-y-6" data-testid="teacher-detail-modal">
      <div className="flex items-start gap-5">
        <div className="h-24 w-24 rounded-2xl overflow-hidden bg-slate-100 flex-shrink-0 grid place-items-center">
          {t.photo_path ? <img src={fileUrl(t.photo_path)} alt={t.name} className="w-full h-full object-cover" /> : <GraduationCap size={32} className="text-slate-400" />}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-2xl font-semibold">{t.name}</h3>
            {t.verified ? (
              <span className="inline-flex items-center gap-1 bg-[color:var(--brand)] text-white rounded-full px-2.5 py-1 text-[11px] font-semibold">
                <ShieldCheck size={11} fill="white" /> Doğrulanmış
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 border border-slate-200 text-slate-500 rounded-full px-2.5 py-1 text-[11px] font-medium">Doğrulanmamış</span>
            )}
          </div>
          <div className="mt-2 text-sm text-slate-600 flex flex-wrap gap-4">
            <span className="flex items-center gap-1"><Mail size={12} /> {t.email || "—"}</span>
            <span className="flex items-center gap-1"><Phone size={12} /> {t.phone || "—"}</span>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 border-t border-slate-200 pt-5">
        <DetailRow label="Yaş" value={t.age} />
        <DetailRow label="Üniversite" value={t.university} />
        <DetailRow label="Bölüm" value={t.department} />
        <DetailRow label="Aktif öğrenci" value={t.active_students} />
        <DetailRow label="Müsait kontenjan" value={t.available_slots} />
      </div>

      {t.subjects?.length > 0 && (
        <div className="border-t border-slate-200 pt-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">Branşlar</div>
          <div className="mt-2 flex flex-wrap gap-2">{t.subjects.map((s) => <Chip key={s}>{s}</Chip>)}</div>
        </div>
      )}

      {t.class_levels?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">Sınıf seviyeleri</div>
          <div className="mt-2 flex flex-wrap gap-2">{t.class_levels.map((c) => <Chip key={c}>{c}</Chip>)}</div>
        </div>
      )}

      {t.lesson_types?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">Ders türü</div>
          <div className="mt-2 flex flex-wrap gap-2">{t.lesson_types.map((l) => <Chip key={l}>{LESSON_TYPE_LABEL[l] || l}</Chip>)}</div>
        </div>
      )}

      {t.intro && (
        <div className="border-t border-slate-200 pt-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">Hakkında</div>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{t.intro}</p>
        </div>
      )}

      {t.yks_doc_path && (
        <div className="border-t border-slate-200 pt-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium mb-2">YKS Belgesi</div>
          <a href={fileUrl(t.yks_doc_path)} target="_blank" rel="noreferrer" className="block max-w-sm">
            <img src={fileUrl(t.yks_doc_path)} alt="YKS Belgesi" className="w-full rounded-lg border border-slate-200" />
          </a>
        </div>
      )}
    </div>
  );
}

function FamilyDetailModal({ f }) {
  return (
    <div className="p-6 space-y-6" data-testid="family-detail-modal">
      <div className="flex items-start gap-5">
        <div className="h-20 w-20 rounded-2xl bg-[color:var(--accent)]/10 grid place-items-center flex-shrink-0">
          <Users size={30} className="text-[color:var(--accent)]" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-2xl font-semibold">{f.name}</h3>
          <div className="mt-2 text-sm text-slate-600 flex flex-wrap gap-4">
            <span className="flex items-center gap-1"><Mail size={12} /> {f.email || "—"}</span>
            <span className="flex items-center gap-1"><Phone size={12} /> {f.phone || "—"}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">{f.child_count || 0} çocuk kayıtlı</div>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-5">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium mb-3">Çocuklar</div>
        {(!f.children || f.children.length === 0) ? (
          <p className="text-sm text-slate-500">Henüz kayıtlı çocuk yok.</p>
        ) : (
          <div className="space-y-3">
            {f.children.map((c, i) => (
              <div key={c.id || i} className="border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid={`child-detail-${i}`}>
                <div className="flex items-center gap-2">
                  <BookOpen size={14} className="text-[color:var(--brand)]" />
                  <div className="font-medium">{c.name}</div>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-white border border-slate-200">{c.class_level}</span>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Dersler</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">{(c.subjects || []).map((s) => <Chip key={s}>{s}</Chip>)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><MapPin size={10} /> Adres</div>
                    <div className="mt-1 text-slate-700">{c.address || "—"}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
